import { CandidateType } from '@bemlo/enums'
import { isTruthy } from '@bemlo/utils'

import { Color } from '../../../constants'
import { Badge, BadgeColor, Button, Text } from '../../../elements'
import { Flex } from '../../../elements/Flex'
import { useTranslations } from '../../../translate'
import { EditIcon } from '../components/EditIcon'
import { useCandidateView } from '../hooks/useCandidateView'

const CANDIDATE_BADGE_COLORS: Record<CandidateType, BadgeColor> = {
  [CandidateType.BOOKED]: 'dark-green',
  [CandidateType.MANAGED]: 'dark-blue',
  [CandidateType.MIRRORED]: 'blue',
  [CandidateType.SOVEREIGN]: 'blue',
  [CandidateType.STATIC]: 'purple',
  [CandidateType.SYNCED]: 'medium-green',
  [CandidateType.EMPLOYED]: 'yellow',
}

export type BiographyHeaderProps = {
  onSendMessageClick: () => void
}

export const BiographyHeader = ({
  onSendMessageClick,
}: BiographyHeaderProps) => {
  const { actor, data, isMe } = useCandidateView()
  const t = useTranslations()

  const {
    fullName,
    homeCountry,
    homeMunicipality,
    hpr,
    profession,
    specializations,
    updatedAt,
  } = data

  const locationText = [
    homeMunicipality,
    homeCountry && t.$country(homeCountry),
  ]
    .filter(isTruthy)
    .join(', ')

  const specializationText =
    specializations?.length >= 1
      ? `${specializations
          .slice(0, 2)
          .map((specialization) => t.$specialization(specialization))
          .join(', ')}${
          specializations.length > 2
            ? ` ${t.candidate_view.biography.and_n_more(
                specializations.length - 2,
              )}`
            : ''
        }`
      : null

  return (
    <Flex items="start" justify="between" styles={{ w: 'full' }}>
      <Flex gap={3.5} items="start">
        <Flex col gap={2} items="start">
          <Flex gap={2} items="center" styles={{ lineHeight: 1, pt: 2 }} wrap>
            <Text bold size={2}>
              {fullName}
            </Text>
            {!isMe && (
              <Badge
                color={CANDIDATE_BADGE_COLORS[data.candidateType]}
                size="sm"
              >
                {t.candidate_view.candidate_type(data.candidateType)}
              </Badge>
            )}
            {profession && (
              <Badge color="blue" size="sm">
                {t.$profession(profession)}
              </Badge>
            )}
            {hpr?.isAuthorized && (
              <Badge color="green" icon="CheckIcon" size="sm">
                {t.candidate_view.base.authorized()}
              </Badge>
            )}
          </Flex>
          <Flex col styles={{ lineHeight: 1.6 }}>
            {specializationText && (
              <Text bold color={Color.BLUE} size={5}>
                {specializationText}
              </Text>
            )}
            {locationText && (
              <Text color={Color.DARK_GREY} size={6}>
                {locationText}
              </Text>
            )}
            {(!isMe || !locationText) && (
              <Text color={Color.DARK_GREY} size={6}>
                {t.candidate_view.base.last_updated()}{' '}
                {t.$date.unix(updatedAt).fromNow()}
              </Text>
            )}
          </Flex>
          {actor.can('contact', 'candidate') && (
            <Button icon="ChatAltIcon" onClick={onSendMessageClick} size="sm">
              {t.candidate_view.biography.send_message()}
            </Button>
          )}
        </Flex>
      </Flex>
      {actor.can('update', 'biography') && (
        <EditIcon permissionScope="biography" />
      )}
    </Flex>
  )
}
